import type {
  MatchOperation,
  MatchSource,
  OpFilterMode,
} from '../types/matchEvents.types';
import type { MatchEventRow } from './matchEventsFeed';

/**
 * Live-hub operation helpers — pure derivation for the `match_operation`
 * side of the feed (the sniper/manual actions) and the active-match `source`
 * badge. Kept free of React / DOM so the classifier, filter predicate, badge
 * mapping, summary formatter and the incident+operation merge are trivially
 * unit-testable. Mirrors the opener's auto (⚡) / manual (✋) split.
 */

/** Automatic vs manual — the two op lanes (mirrors the opener notifications). */
export type OpKind = 'automatic' | 'manual';

/** Op `type`s emitted by the sniper (automatic lane). */
export const AUTOMATIC_OP_TYPES = new Set<string>([
  'sniper_fired',
  'sniper_var_reversal',
  'sniper_fire_blocked',
]);

/** Op `type`s emitted by an operator action (manual lane). */
export const MANUAL_OP_TYPES = new Set<string>(['manual_order']);

/**
 * Resolve an operation's lane. The wire `kind` is authoritative; when it is
 * missing / unrecognised we fall back to the `type` registry (manual types →
 * manual, everything else → automatic), so a new sniper op type still lands in
 * the automatic lane rather than vanishing.
 */
export function classifyOperation(op: {
  kind?: string | null;
  type: string;
}): OpKind {
  if (op.kind === 'automatic') return 'automatic';
  if (op.kind === 'manual') return 'manual';
  if (MANUAL_OP_TYPES.has(op.type)) return 'manual';
  return 'automatic';
}

/** The Auto | Manual | All op-filter predicate. */
export function matchesOpFilter(
  op: { kind?: string | null; type: string },
  mode: OpFilterMode,
): boolean {
  if (mode === 'all') return true;
  const kind = classifyOperation(op);
  return mode === 'auto' ? kind === 'automatic' : kind === 'manual';
}

/** Visual config for an op row (⚡ automatic = blue, ✋ manual = purple). */
export interface OperationDisplay {
  emoji: string;
  color: string;
  label: string;
  kind: OpKind;
}

export function operationDisplay(op: {
  kind?: string | null;
  type: string;
}): OperationDisplay {
  const kind = classifyOperation(op);
  return kind === 'automatic'
    ? { emoji: '⚡', color: '#42a5f5', label: 'automatic', kind }
    : { emoji: '✋', color: '#ab47bc', label: 'manual', kind };
}

/** The provenance badge for an active match (⚡ group / ✋ manual / both). */
export interface SourceBadge {
  emoji: string;
  label: string;
  color: string;
}

export function sourceBadge(source: MatchSource): SourceBadge {
  switch (source) {
    case 'group':
      return { emoji: '⚡', label: 'group', color: '#42a5f5' };
    case 'manual':
      return { emoji: '✋', label: 'manual', color: '#ab47bc' };
    case 'both':
      return { emoji: '⚡✋', label: 'both', color: '#7e57c2' };
    default:
      return { emoji: '•', label: String(source), color: '#9e9e9e' };
  }
}

/** Round to at most 4 decimals and drop trailing zeros ("0.8300" → "0.83"). */
function trimNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return Number(n.toFixed(4)).toString();
}

/** filled/requested size string, or the single side that's present. */
export function formatOperationSize(op: MatchOperation): string {
  const { filled, sizeRequested } = op;
  if (filled != null && sizeRequested != null) {
    return `${trimNumber(filled)}/${trimNumber(sizeRequested)}`;
  }
  if (sizeRequested != null) return trimNumber(sizeRequested);
  if (filled != null) return trimNumber(filled);
  return '';
}

/**
 * One-line human summary of an operation: `venue · market · SIDE · size @ price
 * — reason`. Every segment is optional; missing fields are omitted (no empty
 * separators), and `reason` (when present) is appended after an em dash.
 */
export function formatOperationSummary(op: MatchOperation): string {
  const parts: string[] = [];
  if (op.venue) parts.push(op.venue);
  const market = op.marketLabel ?? op.marketId;
  if (market) parts.push(String(market));
  if (op.side) parts.push(op.side.toUpperCase());
  const size = formatOperationSize(op);
  if (size) parts.push(size);
  const price = op.avgPrice ?? op.price;
  if (price != null) parts.push(`@ ${trimNumber(price)}`);
  let summary = parts.join(' · ');
  if (op.reason) summary = summary ? `${summary} — ${op.reason}` : op.reason;
  return summary;
}

/**
 * A stable de-dupe / React key for an operation (no streamId on the wire).
 * `venue` and `reason` are part of the key so two DISTINCT ops for the same
 * incident/market/side at the same `ts` — e.g. two `sniper_fire_blocked`
 * differing only by venue (Kalshi vs Poly on a null/shared marketId) or by
 * reason — do not collide and get silently dropped by the seen-ops guard.
 */
export function operationKey(op: MatchOperation): string {
  return [
    op.fixtureId,
    op.type,
    op.ts,
    op.incidentKey ?? '',
    op.marketId ?? '',
    op.side ?? '',
    op.venue ?? '',
    op.reason ?? '',
  ].join('|');
}

/**
 * The STABLE identity of an op, or null when it has none. A sniper fire and its
 * later fill re-emits share this id (`sniper:<orderId>`), so a fill-update is
 * reconciled onto the same row instead of appended as a duplicate. Prefer the
 * explicit wire `id`; otherwise derive it from `orderId`. Ops without either
 * (blocked fires, manual orders) return null and keep append-only behavior.
 */
export function operationStableId(op: {
  id?: string | null;
  streamId?: string | null;
  orderId?: string | null;
}): string | null {
  if (op.id) return op.id;
  // Only a sniper FIRE re-emits (its `filled` climbs), and only it carries a
  // globally-unique reconcilable id: `sniper:<orderId>`, set by the relay on
  // BOTH the live and Mongo-backfill paths. A non-fire op's `streamId` is a RAW
  // Redis entry id (`<ms>-<seq>`) that is unique only WITHIN its stream — the
  // automatic (`sniper:notifications`) and manual (`match-ops:manual`) streams
  // have independent id sequences, so two ops sharing a same-ms raw id would
  // collapse onto one row. Non-fire ops never re-emit, so they must fall through
  // to the append-only composite key (`operationKey`) and stay distinct.
  if (op.streamId?.startsWith('sniper:')) return op.streamId;
  // `orderId` is live-only (never persisted); the defensive fallback when a fire
  // arrives before its `sniper:` streamId is set.
  if (op.orderId) return `sniper:${op.orderId}`;
  return null;
}

/**
 * Merge a fill-update onto the existing op for the same stable id. `filled`
 * climbs monotonically as the venue order fills, so a stale/out-of-order frame
 * whose `filled` is strictly LOWER is dropped (the trivial guard); otherwise the
 * incoming frame wins (latest-wins for `filled`/`avgPrice`/`ts`/…). Returns the
 * SAME reference when nothing changed so the caller can skip a state update.
 */
function mergeSniperOp(
  existing: MatchOperation,
  incoming: MatchOperation,
): MatchOperation {
  const existingFilled = existing.filled ?? 0;
  const incomingFilled = incoming.filled ?? 0;
  if (incomingFilled < existingFilled) return existing;
  return { ...existing, ...incoming };
}

/**
 * Fold one incoming `match_operation` into the operations list. An op with a
 * stable id ({@link operationStableId}) that already exists is UPDATED IN PLACE
 * (same index → feed order preserved); everything else is appended, capped at
 * `maxRows` (oldest evicted). Pure — the hook owns the seen-key de-dupe of exact
 * re-deliveries; this handles the fill-climb of a re-emitted fire.
 */
export function reconcileOperations(
  prev: MatchOperation[],
  op: MatchOperation,
  maxRows: number,
): MatchOperation[] {
  const id = operationStableId(op);
  if (id) {
    const idx = prev.findIndex((o) => operationStableId(o) === id);
    if (idx !== -1) {
      const merged = mergeSniperOp(prev[idx], op);
      if (merged === prev[idx]) return prev;
      const next = prev.slice();
      next[idx] = merged;
      return next;
    }
  }
  const next = [...prev, op];
  return next.length > maxRows ? next.slice(next.length - maxRows) : next;
}

/**
 * Bound a seen-op-key Set in place: evict the oldest (insertion-ordered) keys
 * until `size <= max`. Deleting visited entries mid-iteration is safe for a Set.
 * Used to cap the hook's unbounded de-dupe memory over a long session.
 */
export function boundSeenOps(seen: Set<string>, max: number): void {
  if (max < 0) max = 0;
  if (seen.size <= max) return;
  let toEvict = seen.size - max;
  for (const k of seen) {
    if (toEvict-- <= 0) break;
    seen.delete(k);
  }
}

/** A synthetic "end of period" marker (shape mirrors matchEventsFeed). */
export interface FeedMarker {
  key: string;
  label: string;
  receivedAt: string;
}

/** One entry in the merged per-match feed (incident | operation | marker). */
export type MatchFeedEntry =
  | { kind: 'incident'; ts: string; key: string; row: MatchEventRow }
  | { kind: 'operation'; ts: string; key: string; op: MatchOperation }
  | { kind: 'marker'; ts: string; key: string; label: string };

/**
 * Merge incident rows, operations and period markers into ONE feed, ordered
 * newest-first by timestamp (`receivedAt` for incidents/markers, `ts` for
 * operations). Purely a merge + sort — filtering happens upstream.
 */
export function mergeMatchFeed(
  rows: MatchEventRow[],
  operations: MatchOperation[],
  markers: FeedMarker[] = [],
): MatchFeedEntry[] {
  const entries: MatchFeedEntry[] = [
    ...rows.map((row) => ({
      kind: 'incident' as const,
      ts: row.receivedAt,
      key: row.frame.streamId || row.collapseKey,
      row,
    })),
    ...operations.map((op) => ({
      kind: 'operation' as const,
      ts: op.ts,
      // A sniper fire's stable id keeps its row identity stable across fill
      // updates (climb 0 → 78) so the row refreshes in place rather than
      // remounting; ops without one fall back to the composite key.
      key: operationStableId(op) ?? operationKey(op),
      op,
    })),
    ...markers.map((m) => ({
      kind: 'marker' as const,
      ts: m.receivedAt,
      key: m.key,
      label: m.label,
    })),
  ];
  entries.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  return entries;
}
